import React, { useState } from 'react';
import { useChartTooltip } from 'pages/ReportsPage/components/AnalyticsDashboards/OverviewDashboard/useChartTooltip';
import {
  HeatMapContainer,
  HeatMapTable,
  HeatMapHeaderCell,
  HeatMapRowHeader,
  HeatMapRowSublabel,
  HeatMapCell,
} from 'pages/ReportsPage/components/AnalyticsDashboards/FulfillmentDashboard/FulfillmentDashboard.styled';
import { EmptyStateContainer } from 'pages/ReportsPage/components/AnalyticsDashboards/OverviewDashboard/OverviewDashboard.styled';
import { HeatMapGridProps } from 'pages/ReportsPage/components/AnalyticsDashboards/FulfillmentDashboard/FulfillmentDashboard.types';

function parseRgb(color: string): [number, number, number] | null {
  const rgbMatch = color.match(/rgb\((\d+),\s*(\d+),\s*(\d+)\)/);
  if (rgbMatch) return [+rgbMatch[1], +rgbMatch[2], +rgbMatch[3]];
  const hex = color.replace('#', '');
  if (hex.length >= 6) {
    return [
      parseInt(hex.slice(0, 2), 16),
      parseInt(hex.slice(2, 4), 16),
      parseInt(hex.slice(4, 6), 16),
    ];
  }
  return null;
}

function isLightText(bgColor: string): boolean {
  const rgb = parseRgb(bgColor);
  if (!rgb) return false;
  const luminance = (0.299 * rgb[0] + 0.587 * rgb[1] + 0.114 * rgb[2]) / 255;
  return luminance < 0.55;
}

export const HeatMapGrid: React.FC<HeatMapGridProps> = ({
  rowLabels,
  rowSublabels,
  columnLabels,
  data,
  colorScale,
  valueFormatter,
  cellFormatter,
  ariaLabel,
  emptyText = 'No data available',
}) => {
  const tooltip = useChartTooltip();
  const [hoveredCell, setHoveredCell] = useState<string | null>(null);

  if (data.length === 0) {
    return <EmptyStateContainer>{emptyText}</EmptyStateContainer>;
  }

  const maxValue = Math.max(...data.flat().filter((v) => Number.isFinite(v)), 1);

  return (
    <HeatMapContainer>
      <HeatMapTable role="grid" aria-label={ariaLabel}>
        <thead>
          <tr>
            <HeatMapHeaderCell />
            {columnLabels.map((col) => (
              <HeatMapHeaderCell key={col}>{col}</HeatMapHeaderCell>
            ))}
          </tr>
        </thead>
        <tbody>
          {rowLabels.map((row, rowIdx) => (
            <tr key={row}>
              <HeatMapRowHeader>
                {row}
                {rowSublabels?.[rowIdx] && (
                  <HeatMapRowSublabel>{rowSublabels[rowIdx]}</HeatMapRowSublabel>
                )}
              </HeatMapRowHeader>
              {columnLabels.map((col, colIdx) => {
                const value = data[rowIdx]?.[colIdx] ?? 0;
                const cellKey = `${row}-${col}`;
                const bg = colorScale(value, maxValue);
                return (
                  <HeatMapCell
                    key={cellKey}
                    $bgColor={bg}
                    $lightText={isLightText(bg)}
                    role="gridcell"
                    aria-label={`${row}, ${col}: ${valueFormatter(value)}`}
                    style={{ opacity: hoveredCell === cellKey ? 0.8 : 1 }}
                    onMouseEnter={(e) => {
                      setHoveredCell(cellKey);
                      tooltip.show(
                        e,
                        `<strong>${row} — ${col}</strong><br/>${valueFormatter(value)}`,
                      );
                    }}
                    onMouseMove={(e) => tooltip.move(e)}
                    onMouseLeave={() => {
                      setHoveredCell(null);
                      tooltip.hide();
                    }}
                  >
                    {cellFormatter ? cellFormatter(value) : value}
                  </HeatMapCell>
                );
              })}
            </tr>
          ))}
        </tbody>
      </HeatMapTable>
    </HeatMapContainer>
  );
};
